import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Upload, Check, AlertCircle, ImagePlus, X } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { Button } from '../ui';
import { lireLienDepot, envoyerFichier } from '../../services/depotService';

/**
 * Route /depot/:secret — le dépôt de photos par lien à usage limité.
 *
 * POURQUOI CETTE PAGE EXISTE. Un agent conversationnel ne peut pas recevoir de
 * photo. L'utilisateur ouvre donc ce lien sur son téléphone, y choisit ses
 * photos dans sa galerie, elles sont envoyées, et il revient à sa conversation.
 * C'est le seul chemin par lequel une image atteint Cabaza depuis un dialogue.
 *
 * ELLE S'OUVRE SUR UN TÉLÉPHONE, chez quelqu'un qui n'est probablement pas
 * connecté : ni Header ni Footer — ils portent une navigation qui n'a rien à
 * faire ici et inviteraient à quitter la page avant la fin de l'envoi. Le
 * secret dans l'URL est la seule authentification.
 *
 * ELLE NE DIT RIEN DE L'ENTITÉ VISÉE, et c'est délibéré : le serveur ne rend ni
 * titre ni propriétaire, seulement de quoi afficher « déposez vos photos » et
 * combien il en reste. Un lien qui fuiterait ne révélerait donc rien.
 */

/** Les états d'un fichier dans la file d'envoi. */
const EN_ATTENTE = 'attente';
const EN_COURS = 'cours';
const ENVOYE = 'envoye';
const ECHOUE = 'echoue';

/**
 * Une enveloppe autour du File choisi.
 *
 * L'aperçu est une URL d'objet locale : la photo n'a pas encore quitté le
 * téléphone quand on l'affiche. Elle doit être révoquée, sinon chaque sélection
 * abandonne un blob en mémoire.
 */
const enveloppe = (file) => ({
    cle: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    apercu: URL.createObjectURL(file),
    etat: EN_ATTENTE,
    message: ''
});

const DepotPage = () => {
    const { secret } = useParams();
    const { t } = useTranslation();
    const td = t.depot || {};

    const [chargement, setChargement] = useState(true);
    const [lien, setLien] = useState(null);
    const [fichiers, setFichiers] = useState([]);
    const [envoiEnCours, setEnvoiEnCours] = useState(false);
    const [termine, setTermine] = useState(false);
    const inputRef = useRef(null);

    // Les aperçus vivent en dehors du cycle de rendu : on les révoque au
    // démontage à partir d'une référence, jamais depuis l'état — un effet qui
    // dépendrait de `fichiers` révoquerait les URL encore affichées à chaque
    // changement d'état d'un envoi.
    const apercusRef = useRef([]);
    useEffect(() => {
        apercusRef.current = fichiers.map((f) => f.apercu);
    }, [fichiers]);
    useEffect(() => () => {
        apercusRef.current.forEach((url) => URL.revokeObjectURL(url));
    }, []);

    useEffect(() => {
        let vivant = true;
        setChargement(true);

        lireLienDepot(secret).then((resultat) => {
            if (!vivant) return;
            setLien(resultat);
            setChargement(false);
            // Un lien déjà épuisé à l'ouverture est un dépôt terminé, pas une
            // erreur : on le dit avec le message de fin, pas avec un refus.
            if (resultat.valide && resultat.restant <= 0) {
                setTermine(true);
            }
        });

        return () => {
            vivant = false;
        };
    }, [secret]);

    const restant = lien?.restant ?? 0;
    const enAttente = fichiers.filter((f) => f.etat === EN_ATTENTE || f.etat === ECHOUE);
    const nbEnvoyes = fichiers.filter((f) => f.etat === ENVOYE).length;

    /**
     * Ajoute les fichiers choisis, sans dépasser ce qu'il reste à déposer.
     *
     * On tronque ici plutôt que de laisser le serveur refuser : l'utilisateur
     * voit tout de suite ce qui partira, et n'attend pas un envoi condamné.
     */
    const choisir = useCallback((event) => {
        const choisis = Array.from(event.target.files || []);
        if (choisis.length === 0) return;

        setFichiers((precedents) => {
            const dejaEnFile = precedents.filter((f) => f.etat !== ECHOUE).length;
            const place = Math.max(0, restant - dejaEnFile);
            const retenus = choisis.slice(0, place);
            // Les fichiers écartés faute de place ne doivent pas laisser
            // d'URL d'objet derrière eux : on n'en crée que pour les retenus.
            return [...precedents, ...retenus.map(enveloppe)];
        });

        // Permet de re-choisir le même fichier après un retrait.
        event.target.value = '';
    }, [restant]);

    const retirer = useCallback((cle) => {
        setFichiers((precedents) => {
            const cible = precedents.find((f) => f.cle === cle);
            if (cible) URL.revokeObjectURL(cible.apercu);
            return precedents.filter((f) => f.cle !== cle);
        });
    }, []);

    const majFichier = (cle, champs) => {
        setFichiers((precedents) => precedents.map((f) => {
            if (f.cle !== cle) return f;
            return { ...f, ...champs };
        }));
    };

    /**
     * Envoie les fichiers UN PAR UN — la route n'en accepte qu'un.
     *
     * Un échec n'interrompt pas la boucle : le fichier est marqué, on passe au
     * suivant. Quelqu'un qui dépose huit photos et dont la troisième est trop
     * lourde doit voir arriver les sept autres, pas tout perdre.
     *
     * Le compteur restant est décrémenté après CHAQUE succès, en préférant la
     * valeur du serveur quand il la donne : c'est lui qui fait autorité, notre
     * décompte n'est qu'un repli s'il se tait.
     */
    const envoyer = async () => {
        if (envoiEnCours) return;
        setEnvoiEnCours(true);

        // Photographie de la file au moment du clic : les états changent
        // pendant la boucle, on itère donc sur une liste figée.
        const aEnvoyer = fichiers.filter((f) => f.etat === EN_ATTENTE || f.etat === ECHOUE);
        let restantLocal = restant;

        for (const item of aEnvoyer) {
            if (restantLocal <= 0) {
                majFichier(item.cle, {
                    etat: ECHOUE,
                    message: td.quotaAtteint || 'Le nombre de photos autorisé est atteint.'
                });
                continue;
            }

            majFichier(item.cle, { etat: EN_COURS, message: '' });

            // eslint-disable-next-line no-await-in-loop
            const resultat = await envoyerFichier(secret, item.file);

            if (resultat.success) {
                if (resultat.restant !== null) {
                    restantLocal = resultat.restant;
                } else {
                    restantLocal = Math.max(0, restantLocal - 1);
                }
                majFichier(item.cle, { etat: ENVOYE, message: '' });
                setLien((precedent) => ({ ...precedent, restant: restantLocal }));
            } else {
                majFichier(item.cle, { etat: ECHOUE, message: resultat.message });
            }
        }

        setEnvoiEnCours(false);

        // Terminé quand il ne reste rien à déposer, ou rien à envoyer qui
        // n'ait déjà échoué : dans les deux cas l'utilisateur peut repartir.
        setFichiers((precedents) => {
            const resteAEnvoyer = precedents.some((f) => f.etat === EN_ATTENTE);
            const auMoinsUnEnvoye = precedents.some((f) => f.etat === ENVOYE);
            if (restantLocal <= 0 || (!resteAEnvoyer && auMoinsUnEnvoye)) {
                setTermine(true);
            }
            return precedents;
        });
    };

    const cadre = (contenu) => (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center px-4 py-10">
            <div className="w-full max-w-md">{contenu}</div>
        </div>
    );

    if (chargement) {
        return cadre(
            <div className="flex items-center justify-center py-16" data-testid="depot-loading">
                <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
                <span className="ml-3 text-gray-500 dark:text-gray-400">
                    {t.common?.loading || 'Chargement...'}
                </span>
            </div>
        );
    }

    // Lien inconnu, expiré ou épuisé. Le serveur sait dire lequel et le dit
    // mieux que nous : on relaie son message, sobrement, sans bouton — il n'y
    // a rien à réessayer, et proposer une action serait mentir.
    if (!lien?.valide) {
        return cadre(
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 text-center"
                data-testid="depot-invalide"
            >
                <AlertCircle className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">
                    {lien?.message || td.lienInvalide || 'Ce lien de dépôt n\'est plus valable.'}
                </p>
            </div>
        );
    }

    if (termine) {
        return cadre(
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 text-center"
                data-testid="depot-termine"
            >
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-gray-800 dark:text-gray-100 font-medium">
                    {td.termine || 'Photos envoyees, vous pouvez revenir a votre conversation.'}
                </p>
                {nbEnvoyes > 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {nbEnvoyes} {nbEnvoyes > 1
                            ? (td.photosEnvoyees || 'photos envoyées')
                            : (td.photoEnvoyee || 'photo envoyée')}
                    </p>
                )}
            </div>
        );
    }

    return cadre(
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 sm:p-8" data-testid="depot-page">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {td.titre || 'Déposez vos photos'}
            </h1>

            {/* Le libellé du module — « Moments », par exemple — situe le dépôt
                sans rien dire de l'entité elle-même. */}
            {lien.label && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{lien.label}</p>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                {restant > 1
                    ? `${restant} ${td.restantPluriel || 'photos peuvent encore être déposées.'}`
                    : `${restant} ${td.restantSingulier || 'photo peut encore être déposée.'}`}
            </p>

            {/* accept="image/*" + multiple : sur mobile, le système propose
                lui-même l'appareil photo ou la galerie. Pas de capture="..."
                — l'imposer priverait de la galerie, qui est le cas d'usage. */}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={choisir}
                className="hidden"
                data-testid="depot-input"
            />

            <Button
                variant="secondary"
                fullWidth
                icon={ImagePlus}
                onClick={() => inputRef.current?.click()}
                disabled={envoiEnCours || enAttente.length >= restant}
                className="mb-4"
            >
                {td.choisir || 'Choisir des photos'}
            </Button>

            {fichiers.length > 0 && (
                <ul className="space-y-2 mb-6" data-testid="depot-apercus">
                    {fichiers.map((item) => (
                        <li
                            key={item.cle}
                            className="flex items-center gap-3 p-2 rounded-xl bg-gray-50 dark:bg-gray-700/50"
                        >
                            <img
                                src={item.apercu}
                                alt=""
                                className="w-12 h-12 rounded-lg object-cover shrink-0"
                            />
                            <div className="min-w-0 grow">
                                <p className="text-sm text-gray-800 dark:text-gray-100 truncate">
                                    {item.file.name}
                                </p>
                                {item.etat === ECHOUE && item.message && (
                                    <p className="text-xs text-red-600 dark:text-red-400">{item.message}</p>
                                )}
                            </div>

                            <div className="shrink-0">
                                {item.etat === EN_COURS && (
                                    <Loader2 className="w-5 h-5 animate-spin text-cabaza-purple" />
                                )}
                                {item.etat === ENVOYE && (
                                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                )}
                                {item.etat === ECHOUE && (
                                    <AlertCircle className="w-5 h-5 text-red-500" />
                                )}
                                {item.etat === EN_ATTENTE && !envoiEnCours && (
                                    <button
                                        type="button"
                                        onClick={() => retirer(item.cle)}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                        aria-label={td.retirer || 'Retirer'}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <Button
                variant="primary"
                fullWidth
                icon={Upload}
                loading={envoiEnCours}
                disabled={envoiEnCours || enAttente.length === 0}
                onClick={envoyer}
                data-testid="depot-envoyer"
            >
                {envoiEnCours
                    ? `${td.envoiEnCours || 'Envoi...'} ${nbEnvoyes + 1}/${nbEnvoyes + enAttente.length}`
                    : (td.envoyer || 'Envoyer')}
            </Button>
        </div>
    );
};

export default DepotPage;
